import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
  useWindowDimensions,
  Modal,
  TextInput,
  Pressable,
} from "react-native";
import { useNavigation, useRoute, RouteProp, useFocusEffect } from "@react-navigation/native";
import { formatPct } from '../../utils/formatPercentage';
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import Svg, { Path, Circle } from "react-native-svg";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  PlusIcon,
  EditIcon,
  TrashIcon,
  XIcon,
  ExportIcon,
} from "../../components/Icons";
import { COLORS, FONTS } from "../../constants/config";
import { PageContainer, Navbar, Breadcrumb, EvidenceTable } from "../../components";
import { GoalsSkeleton } from "../../components/Skeleton";
import PendingDeletionBadge from "../../components/PendingDeletionBadge";
import DeletionImpactModal from "../../components/DeletionImpactModal";
import {
  useResponsive,
  useRoleNavigation,
  useAutoTranslate,
} from "../../hooks";
import { MainStackParamList } from "../../navigation/MainNavigator";
import { processesApi, subProcessesApi, goalsApi, kpisApi } from "../../services/apiService";
import { useProcessesQuery } from "../../hooks/queries/useProcessesQuery";
import { OrganizationFilterDropdown, useCanViewAllOrgs } from "../../components/OrganizationFilterDropdown";
import { DropdownPortal, useDropdownAnchor } from "../../components/DropdownPortal";
import { queryKeys } from "../../providers/QueryProvider";
import { downloadBlobAsFile } from "../../utils/downloadFile";
import { ProcessList, StrategicGoalList } from "../../types";
import { useNotification } from "../../contexts/NotificationContext";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { getTextAlign } from "../../utils/rtl";
import { useYear } from "../../contexts/YearContext";

import { tokens } from '@design';
import { IconButton } from '@design/components/IconButton';
import { Button } from '@design/components/Button';
import { PageHeader } from '@design/components/PageHeader';
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// SVG Icons
// ============================================================
const FileInputIcon = ({ size = 13, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V7L15 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V8" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M14 2V8H20" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M2 15H10" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 13L10 15L8 17" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const HomeIcon = ({
  size = 16,
  color = tokens.colors.raw.gray["500"],
}: {
  size?: number;
  color?: string;
}) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const MoreVertIcon = ({
  size = 16,
  color = tokens.colors.raw.gray["500"],
}: {
  size?: number;
  color?: string;
}) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Circle cx="12" cy="5" r="1" fill={color} />
    <Circle cx="12" cy="12" r="1" fill={color} />
    <Circle cx="12" cy="19" r="1" fill={color} />
  </Svg>
);

// Donut Chart Component
const DonutChart = ({
  percentage,
  size = 60,
  strokeWidth = 6,
  textColor = COLORS.neutral900,
}: {
  percentage: number;
  size?: number;
  strokeWidth?: number;
  textColor?: string;
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const center = size / 2;

  // Color based on achievement percentage
  // >= 100%: Green (ممتاز), >= 80%: Orange (جيد), < 80%: Red (يحتاج تحسين)
  let color: string = tokens.colors.raw.red["500"]; // Red - Needs Improvement (< 80%)
  if (percentage >= 100)
    color = tokens.colors.raw.green["500"]; // Green - Excellent
  else if (percentage >= 80) color = tokens.colors.raw.orange["500"]; // Orange - Good

  // Calculate dot position at END of progress arc
  // -90 offset to start from top (12 o'clock position)
  const angle = ((percentage / 100) * 360 - 90) * (Math.PI / 180);
  const dotX = center + radius * Math.cos(angle);
  const dotY = center + radius * Math.sin(angle);

  return (
    <View
      style={{
        width: size,
        height: size,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Svg width={size} height={size}>
        {/* Background circle */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={tokens.colors.raw.gray["200"]}
          strokeWidth={strokeWidth}
          fill="transparent"
        />
        {/* Progress arc - rotation applied to this element only */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={color}
          strokeWidth={strokeWidth}
          fill="transparent"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
        />
        {/* Dot at end of progress - ONLY show if percentage > 0 */}
        {percentage > 0 && (
          <Circle
            cx={dotX}
            cy={dotY}
            r={strokeWidth / 2 + 2}
            fill={color}
            stroke="#FCFCFC"
            strokeWidth={2}
          />
        )}
      </Svg>
      <View
        style={{
          position: "absolute",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Text
          style={{
            fontSize: tokens.typography.paragraph["paragraph font size"],
            fontFamily: FONTS.numbers,
            fontWeight: "500",
            color: textColor,
          }}
        >
          %{percentage}
        </Text>
      </View>
    </View>
  );
};

type RouteProps = RouteProp<MainStackParamList, 'Processes'>;

export default function ProcessesScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const organizationId = route.params?.organizationId;
  const organizationName = route.params?.organizationName;
  const { isMobile, isTablet, isDesktop, isSmallMobile, containerPadding } =
    useResponsive();
  const { showError, showSuccess } = useNotification();
  const { user } = useAuth();
  const isOrgEmployee = user?.profile?.user_type === 'org_employee';
  const { navigateToHome, isViewOnly } = useRoleNavigation();

  // Evidence role flags
  const canManageEvidence = ['org_employee', 'coordinator', 'superuser'].includes(user?.profile?.user_type || '');
  const ownDeptId = user?.profile?.organization;
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const rtlRow = isRTL ? 'row-reverse' as const : 'row' as const;
  const { selectedYear } = useYear();
  const { translate, translateOrg } = useAutoTranslate();

  // Themed styles for dark mode support
  const themedCardStyles = {
    card: { backgroundColor: colors.card, borderColor: colors.cardBorder },
    cardTitle: { color: colors.textPrimary },
    cardValue: { color: colors.text },
    cardDescription: { color: colors.textMuted },
    sectionTitle: { color: colors.textPrimary },
    pageSubtitle: { color: colors.textMuted },
  };

  const [refreshing, setRefreshing] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState<number | null>(null);
  const [showGoalDropdown, setShowGoalDropdown] = useState(false);
  const { ref: goalTriggerRef, pos: goalDropdownPos, measure: measureGoalTrigger } = useDropdownAnchor();
  const [searchQuery, setSearchQuery] = useState("");
  const [kpiCounts, setKpiCounts] = useState<Record<number, number>>({});
  const [activeMenuId, setActiveMenuId] = useState<number | null>(null);
  const [showDeletionImpact, setShowDeletionImpact] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  const canViewAllOrgs = useCanViewAllOrgs();
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(organizationId ?? null);
  const effectiveOrgId = selectedOrgId ?? organizationId ?? undefined;

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedOrgId]);

  // Responsive - using the hook
  const isSmallScreen = isMobile;
  const isMediumScreen = isTablet;

  const queryClient = useQueryClient();

  // Main data query — React Query shows cached data instantly on focus,
  // refetches silently in the background (stale-while-revalidate).
  const {
    data: processesData,
    isLoading: loading,
    refetch,
  } = useProcessesQuery({
    page: currentPage,
    pageSize: itemsPerPage,
    department_id: effectiveOrgId,
    strategic_goals: selectedGoal ?? undefined,
    search: searchQuery.trim() || undefined,
    year: selectedYear,
  });

  const processes: ProcessList[] = useMemo(
    () => processesData?.results || [],
    [processesData],
  );
  const totalCount = processesData?.count || 0;
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  // Goals dropdown — separate query, rarely changes.
  const { data: goalsDropdownData } = useQuery({
    queryKey: ['goals', 'dropdown', effectiveOrgId ?? null],
    queryFn: async () => {
      const goalsParams: Record<string, any> = { page_size: 100 };
      if (effectiveOrgId) goalsParams.department_id = effectiveOrgId;
      return goalsApi.list(goalsParams);
    },
    staleTime: 10 * 60 * 1000,
  });
  const goals: StrategicGoalList[] = goalsDropdownData?.results || [];

  // After processes load, populate the per-process KPI counts. This is still
  // a fan-out of 1 request per process, but it runs in the background after
  // the main list has already rendered — no spinner on focus.
  useEffect(() => {
    if (!processes.length) {
      setKpiCounts({});
      return;
    }
    let cancelled = false;
    (async () => {
      const counts = await Promise.all(
        processes.map(async (p) => {
          try {
            const kpis = await kpisApi.list({ source_process: p.id, page_size: 1 });
            return { id: p.id, count: kpis.count || 0 };
          } catch {
            return { id: p.id, count: 0 };
          }
        }),
      );
      if (cancelled) return;
      const countsMap: Record<number, number> = {};
      counts.forEach((c) => {
        countsMap[c.id] = c.count;
      });
      setKpiCounts(countsMap);
    })();
    return () => {
      cancelled = true;
    };
  }, [processes]);

  // Refresh on focus — cached data renders instantly, refetch runs silently.
  useFocusEffect(
    useCallback(() => {
      refetch();
    }, [refetch]),
  );

  // Reset to page 1 when year changes; query key change triggers a refetch.
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedYear]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await refetch();
    setRefreshing(false);
  }, [refetch]);

  // Back-compat shim for handlers that still call fetchData() — invalidates
  // the React Query cache so the next access triggers a refetch.
  const fetchData = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.processes.all });
  }, [queryClient]);

  const handleApplyFilter = () => {
    setCurrentPage(1);
    setShowGoalDropdown(false);
  };

  const handleClearFilter = () => {
    setSelectedGoal(null);
    setSearchQuery("");
    setCurrentPage(1);
  };

  const handleExportExcel = async () => {
    if (totalCount === 0) {
      showError(t('common.noDataToExport'));
      return;
    }
    setIsExporting(true);
    try {
      // Backend builds the report (processes + sub-processes + KPI sheet), same filters.
      const params: Record<string, any> = { lang: language };
      if (effectiveOrgId) params.department_id = effectiveOrgId;
      if (selectedGoal) params.strategic_goals = selectedGoal;
      if (searchQuery) params.search = searchQuery;
      if (selectedYear) params.year = selectedYear;

      const response = await processesApi.exportExcel(params);
      downloadBlobAsFile(response.data, `${t('exportFiles.processes')}.xlsx`);
      showSuccess(t('messages.exportSuccess'));
    } catch (error) {
      console.error('Export error:', error);
      showError(t('messages.exportError'));
    } finally {
      setIsExporting(false);
    }
  };

  const handleAddProcess = () => {
    navigation.navigate("AddProcess" as any);
  };

  const handleViewDetails = (id: number) => {
    navigation.navigate("ProcessDetail", { id });
  };

  const toggleMenu = (id: number) => {
    setActiveMenuId(activeMenuId === id ? null : id);
  };

  const handleEdit = (id: number) => {
    setActiveMenuId(null);
    navigation.navigate("AddProcess" as any, { processId: id });
  };

  const handleDeleteClick = (id: number) => {
    setActiveMenuId(null);
    setDeleteConfirmId(id);
    setShowDeletionImpact(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmId) return;

    setDeleting(true);
    try {
      const response = await processesApi.delete(deleteConfirmId);

      if (response?.status === 202 || response?.data?.status === 'pending') {
        showSuccess(t('messages.deleteRequestSent'));
      } else {
        showSuccess(t("processes.deleteSuccess"));
      }

      fetchData();
    } catch (err: any) {
      if (err.response?.status === 202) {
        showSuccess(t('messages.deleteRequestSent'));
        fetchData();
      } else if (err.response?.status === 409) {
        showError(t('deletion.alreadyPending'));
      } else {
        showError(err.response?.data?.detail || t("processes.deleteFailed"));
      }
    } finally {
      setDeleting(false);
      setShowDeletionImpact(false);
      setDeleteConfirmId(null);
    }
  };

  // Calculate achievement percentage from API data
  const getAchievementPercentage = (process: ProcessList) => {
    // Use achievement_percentage from API if available
    if (process.achievement_percentage !== undefined) {
      return parseFloat(Number(process.achievement_percentage).toFixed(2));
    }
    // Fallback: calculate based on weight or return 0
    const weight = parseFloat(process.weight || "0");
    return weight > 0 ? Math.min(Math.round(weight), 100) : 0;
  };

  // Full-page skeleton only on cold load (no cached data yet). Warm cache
  // and background refetches fall through to the regular render — matches
  // GoalsScreen / InitiativesScreen pattern.
  if (loading && processes.length === 0) {
    return (
      <PageContainer>
        <Navbar />
        <GoalsSkeleton />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Navbar />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            colors={[COLORS.primary]}
          />
        }
      >
        <View style={[styles.container, { padding: containerPadding }]}>
          {/* Breadcrumb */}
          <Breadcrumb
            items={organizationId ? [
              { label: t("navigation.home") },
              { label: t("nav.organizations"), route: "Organizations" },
              { label: organizationName || "", route: "OrganizationDetail", params: { id: organizationId } },
              { label: t("processes.title") },
            ] : [
              { label: t("navigation.home") },
              { label: t("processes.title") },
            ]}
          />

          <PageHeader
            title={t("processes.title")}
            actions={
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: tokens.spacing.absolute["2"] }}>
                {!isViewOnly && (
                  <Button
                    variant="primary"
                    onPress={handleAddProcess}
                    trailingIcon={<PlusIcon size={16} color={tokens.colors.raw.white} />}
                  >
                    {t("processes.addMainProcess")}
                  </Button>
                )}
                {!isViewOnly && (
                  <Button
                    variant="secondary"
                    onPress={() => navigation.navigate('ImportProcesses' as any)}
                    trailingIcon={<FileInputIcon size={16} color={colors.textPrimary} />}
                  >
                    {t('importProcesses.title')}
                  </Button>
                )}
                <Button
                  variant="secondary"
                  onPress={handleExportExcel}
                  disabled={isExporting}
                  trailingIcon={<ExportIcon size={16} color={colors.textPrimary} />}
                >
                  {t('common.exportExcel')}
                </Button>
              </View>
            }
            contentTrailing={
            <View style={styles.filterRow}>
              {/* Clear Filter Button - Only shown when filter is active */}
              {(selectedGoal || searchQuery) && (
                <TouchableOpacity
                  style={styles.clearFilterButton}
                  onPress={handleClearFilter}
                >
                  <XIcon size={14} color={tokens.colors.raw.red["500"]} />
                  <Text style={styles.clearFilterText}>
                    {t("filters.clearFilter")}
                  </Text>
                </TouchableOpacity>
              )}

              <TouchableOpacity
                style={[
                  styles.applyFilterButton,
                  { backgroundColor: isDark ? tokens.colors.raw.gray["700"] : tokens.colors.raw.slate["100"] },
                ]}
                onPress={handleApplyFilter}
              >
                <Text
                  style={[
                    styles.applyFilterText,
                    { color: isDark ? tokens.colors.raw.gray["50"] : tokens.colors.raw.gray["700"] },
                  ]}
                >
                  {t("common.applyFilter")}
                </Text>
              </TouchableOpacity>

              <View style={styles.dropdownContainer}>
                <TextInput
                  style={[
                    styles.searchInput,
                    {
                      backgroundColor: colors.card,
                      borderColor: colors.cardBorder,
                      color: colors.text,
                    },
                  ]}
                  value={searchQuery}
                  onChangeText={setSearchQuery}
                  placeholder={t("processes.searchPlaceholder")}
                  placeholderTextColor={colors.textSecondary}
                  textAlign="right"
                />
              </View>

              <View style={styles.dropdownContainer}>
                <TouchableOpacity
                  style={[
                    styles.dropdown,
                    {
                      backgroundColor: colors.card,
                      borderColor: colors.cardBorder,
                    },
                  ]}
                  onPress={() => { if (!showGoalDropdown) measureGoalTrigger(); setShowGoalDropdown(!showGoalDropdown); }}
                  ref={goalTriggerRef as any}
                >
                  <ChevronDownIcon size={16} color={colors.textSecondary} />
                  <Text style={[styles.dropdownText, { color: colors.text }]}>
                    {selectedGoal
                      ? translate(
                          goals.find((g) => g.id === selectedGoal)?.title,
                        ) || t("kpis.allGoals")
                      : t("kpis.allGoals")}
                  </Text>
                </TouchableOpacity>

                <DropdownPortal
                  open={showGoalDropdown}
                  onClose={() => setShowGoalDropdown(false)}
                  pos={goalDropdownPos}
                  backgroundColor={colors.card}
                  borderColor={colors.cardBorder}
                  maxHeight={300}
                >
                  <ScrollView nestedScrollEnabled style={{ maxHeight: 280 }}>
                    <TouchableOpacity
                      style={[
                        styles.dropdownItem,
                        { borderBottomColor: colors.cardBorder },
                      ]}
                      onPress={() => {
                        setSelectedGoal(null);
                        setShowGoalDropdown(false);
                      }}
                    >
                      <Text
                        style={[
                          styles.dropdownItemText,
                          { color: colors.text },
                        ]}
                      >
                        {t("kpis.allGoals")}
                      </Text>
                    </TouchableOpacity>
                    {goals.map((goal) => (
                      <TouchableOpacity
                        key={goal.id}
                        style={[
                          styles.dropdownItem,
                          { borderBottomColor: colors.cardBorder },
                        ]}
                        onPress={() => {
                          setSelectedGoal(goal.id);
                          setShowGoalDropdown(false);
                        }}
                      >
                        <Text
                          style={[
                            styles.dropdownItemText,
                            { color: colors.text },
                          ]}
                        >
                          {translate(goal.title)}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </ScrollView>
                </DropdownPortal>
              </View>

              {/* Organization filter - rightmost in RTL = first reading position */}
              {canViewAllOrgs && (
                <View style={styles.dropdownContainer}>
                  <OrganizationFilterDropdown
                    selectedOrgId={selectedOrgId}
                    onChange={setSelectedOrgId}
                  />
                </View>
              )}
            </View>
            }
            style={{ marginBottom: tokens.spacing.absolute["6"] }}
          />

          {/* Process Cards Grid */}
          <View
            style={[
              styles.cardsGrid,
              isSmallScreen && styles.cardsGridSmall,
              { zIndex: 1 },
            ]}
          >
            {processes.map((process) => {
              const percentage = getAchievementPercentage(process);
              const isPending = process.approval_status === 'pending';
              return (
                <View
                  key={process.id}
                  style={[
                    styles.card,
                    isSmallScreen && styles.cardSmall,
                    themedCardStyles.card,
                    activeMenuId === process.id && { zIndex: 1000, position: 'relative' },
                  ]}
                >
                  <View style={styles.cardHeader}>
                    <View style={styles.cardTitleSection}>
                      <Text
                        style={[styles.cardTitle, themedCardStyles.cardTitle]}
                      >
                        {translate(process.name)}
                      </Text>
                      {isPending && (
                        <View style={styles.pendingBadge}>
                          <Text style={styles.pendingBadgeText}>{t('processes.pendingApproval')}</Text>
                        </View>
                      )}
                      {process.has_pending_deletion && <PendingDeletionBadge compact />}
                      {(process.organizational_units_details || []).map(u => (
                        <View key={u.id} style={styles.badge}>
                          <Text style={styles.badgeText}>
                            {translate(u.name_ar || u.name)}
                          </Text>
                        </View>
                      ))}
                      <Text
                        style={[
                          styles.cardDescription,
                          themedCardStyles.cardDescription,
                        ]}
                        numberOfLines={2}
                      >
                        {translate(process.description) ||
                          t("common.noDescription")}
                      </Text>
                    </View>
                    <DonutChart
                      percentage={percentage}
                      size={80}
                      strokeWidth={7}
                      textColor={colors.textPrimary}
                    />
                  </View>

                  {/* Stats Row */}
                  <View style={styles.statsRow}>
                    <View
                      style={[
                        styles.statBox,
                        { backgroundColor: isDark ? tokens.colors.raw.gray["700"] : tokens.colors.raw.slate["100"] },
                      ]}
                    >
                      <Text
                        style={[
                          styles.statValue,
                          { color: isDark ? tokens.colors.raw.gray["50"] : "#171717" },
                        ]}
                      >
                        {process.sub_processes_count || 0}
                      </Text>
                      <Text
                        style={[
                          styles.statLabel,
                          { color: isDark ? tokens.colors.raw.gray["400"] : tokens.colors.raw.gray["500"] },
                        ]}
                      >
                        {t("processes.subProcessesCount")}
                      </Text>
                    </View>
                    <View
                      style={[
                        styles.statBox,
                        { backgroundColor: isDark ? tokens.colors.raw.gray["700"] : tokens.colors.raw.slate["100"] },
                      ]}
                    >
                      <Text
                        style={[
                          styles.statValue,
                          { color: isDark ? tokens.colors.raw.gray["50"] : "#171717" },
                        ]}
                      >
                        {kpiCounts[process.id] || 0}
                      </Text>
                      <Text
                        style={[
                          styles.statLabel,
                          { color: isDark ? tokens.colors.raw.gray["400"] : tokens.colors.raw.gray["500"] },
                        ]}
                      >
                        {t("processes.kpisCount")}
                      </Text>
                    </View>
                    {/* Relative weight hidden per user request */}
                    {/* <View style={[styles.statBox, { backgroundColor: isDark ? tokens.colors.raw.gray["700"] : tokens.colors.raw.slate["100"] }]}>
                      <Text style={[styles.statValue, { color: isDark ? tokens.colors.raw.gray["50"] : '#171717' }]}>{parseFloat(process.weight || '0').toFixed(2)} %</Text>
                      <Text style={[styles.statLabel, { color: isDark ? tokens.colors.raw.gray["400"] : tokens.colors.raw.gray["500"] }]}>{t('common.relativeWeight')}</Text>
                    </View> */}
                  </View>

                  {/* Card Footer */}
                  <View
                    style={[
                      styles.cardFooter,
                      { borderTopColor: colors.cardBorder },
                      isViewOnly && { justifyContent: 'flex-end' },
                    ]}
                  >
                    {/* Hide menu for view-only users (org_executive, executive, strategy_team) */}
                    {!isViewOnly && (
                      <View style={styles.menuContainer}>
                        <IconButton
                          variant="ghost"
                          size="sm"
                          icon={<MoreVertIcon color={colors.textSecondary} />}
                          accessibilityLabel={t('common.menu')}
                          onPress={() => toggleMenu(process.id)}
                        />
                        {activeMenuId === process.id && (
                        <View
                          style={[
                            styles.menuDropdown,
                            {
                              backgroundColor: colors.card,
                              borderColor: colors.cardBorder,
                            },
                          ]}
                        >
                          <TouchableOpacity
                            style={[
                              styles.menuItem,
                              { borderBottomColor: colors.cardBorder },
                            ]}
                            onPress={() => handleEdit(process.id)}
                          >
                            <Text
                              style={[
                                styles.menuItemText,
                                { color: colors.text },
                              ]}
                            >
                              {t("common.edit")}
                            </Text>
                            <EditIcon size={16} color={colors.textSecondary} />
                          </TouchableOpacity>
                          <TouchableOpacity
                            style={[styles.menuItem, styles.menuItemDanger]}
                            onPress={() => handleDeleteClick(process.id)}
                          >
                            <Text style={styles.menuItemTextDanger}>
                              {t("common.delete")}
                            </Text>
                            <TrashIcon size={16} color={tokens.colors.raw.red["500"]} />
                          </TouchableOpacity>
                        </View>
                        )}
                      </View>
                    )}
                    <TouchableOpacity
                      style={styles.detailsLink}
                      onPress={() => handleViewDetails(process.id)}
                    >
                      <Text
                        style={[
                          styles.detailsLinkText,
                          { color: colors.textSecondary },
                        ]}
                      >
                        {t("common.viewDetails")}
                      </Text>
                      <ChevronLeftIcon size={14} color={colors.textSecondary} />
                    </TouchableOpacity>
                  </View>
                </View>
              );
            })}
          </View>

          {/* Empty State */}
          {processes.length === 0 && (
            <View style={styles.emptyState}>
              <Text
                style={[styles.emptyStateText, { color: colors.textMuted }]}
              >
                {t("processes.noProcesses")}
              </Text>
              {/* Hide add button for view-only users (executive) */}
              {!isViewOnly && (
                <Button
                  variant="primary"
                  onPress={handleAddProcess}
                  leadingIcon={<PlusIcon size={16} color={tokens.colors.raw.white} />}
                >
                  {t("processes.addNewProcess")}
                </Button>
              )}
            </View>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <View style={[styles.pagination, { flexDirection: rtlRow }]}>
              <TouchableOpacity
                style={[
                  styles.paginationButton,
                  currentPage === 1 && styles.paginationButtonDisabled,
                ]}
                onPress={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <Text
                  style={[
                    styles.paginationButtonText,
                    { color: colors.text },
                    currentPage === 1 && { color: colors.textMuted },
                  ]}
                >
                  {t("common.previous")}
                </Text>
                <ChevronRightIcon
                  size={14}
                  color={currentPage === 1 ? colors.textMuted : colors.text}
                />
              </TouchableOpacity>

              <View style={[styles.paginationNumbers, { flexDirection: rtlRow }]}>
                {(() => {
                  const maxVisible = 5;
                  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                  const end = Math.min(totalPages, start + maxVisible - 1);
                  if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
                  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
                })().map((page) => (
                  <TouchableOpacity
                    key={page}
                    style={[
                      styles.paginationNumber,
                      { backgroundColor: isDark ? tokens.colors.raw.gray["700"] : tokens.colors.raw.gray["100"] },
                      currentPage === page && styles.paginationNumberActive,
                    ]}
                    onPress={() => setCurrentPage(page)}
                  >
                    <Text
                      style={[
                        styles.paginationNumberText,
                        { color: isDark ? tokens.colors.raw.gray["50"] : tokens.colors.raw.gray["700"] },
                        currentPage === page &&
                          styles.paginationNumberTextActive,
                      ]}
                    >
                      {page}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>

              <TouchableOpacity
                style={[
                  styles.paginationButton,
                  currentPage === totalPages && styles.paginationButtonDisabled,
                ]}
                onPress={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={currentPage === totalPages}
              >
                <ChevronLeftIcon
                  size={14}
                  color={currentPage === totalPages ? colors.textMuted : colors.text}
                />
                <Text
                  style={[
                    styles.paginationButtonText,
                    { color: colors.text },
                    currentPage === totalPages && { color: colors.textMuted },
                  ]}
                >
                  {t("common.next")}
                </Text>
              </TouchableOpacity>
            </View>
          )}

          <EvidenceTable
            relatedType="process_collection"
            filterRelatedId={canViewAllOrgs ? undefined : (ownDeptId ?? undefined)}
            title={t('evidence.sectionTitle')}
            canManage={canManageEvidence && !!ownDeptId}
            showOrgColumn={canViewAllOrgs}
            onAddPress={() => navigation.navigate('AddEvidence', {
              relatedType: 'process_collection',
              relatedId: ownDeptId as number,
              contextLabel: t('processes.title'),
            })}
            onEditPress={(ev) => navigation.navigate('AddEvidence', {
              evidenceId: ev.id,
              relatedType: ev.related_type,
              relatedId: ev.related_id,
              contextLabel: t('processes.title'),
            })}
          />
        </View>
      </ScrollView>

      {/* Deletion Impact Modal */}
      <DeletionImpactModal
        visible={showDeletionImpact}
        onClose={() => { setShowDeletionImpact(false); setDeleteConfirmId(null); }}
        onConfirm={handleConfirmDelete}
        entityName={processes.find(p => p.id === deleteConfirmId)?.name || ''}
        fetchImpact={async () => {
          const response = await processesApi.deletionImpact(deleteConfirmId!);
          return response.data;
        }}
        isOrgEmployee={isOrgEmployee}
        isSubmitting={deleting}
      />
    </PageContainer>
  );
}

const getStyles = (isRTL: boolean) => StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  container: {
    flex: 1,
    padding: tokens.spacing.absolute["6"],
    paddingTop: tokens.spacing.absolute["4"],
  },
  containerSmall: {
    padding: tokens.spacing.absolute["4"],
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  loadingText: {
    marginTop: tokens.spacing.absolute["3"],
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: COLORS.neutral600,
  },

  // Breadcrumb
  breadcrumb: {
    flexDirection: "row-reverse",
    alignItems: "center",
    marginBottom: tokens.spacing.absolute["4"],
    gap: tokens.spacing.absolute["2"],
  },
  breadcrumbItem: {
    flexDirection: "row",
    alignItems: "center",
  },
  breadcrumbText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
  },
  breadcrumbActive: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: COLORS.primary,
  },

  // Filter Row
  filterRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: tokens.spacing.absolute["5"],
    zIndex: 1000,
  },
  dropdownContainer: {
    position: "relative",
    zIndex: 9999,
  },
  dropdownOverlay: {
    position: "fixed" as any,
    top: 0,
    start: 0,
    end: 0,
    bottom: 0,
    zIndex: 9998,
  },
  searchInput: {
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    minWidth: 200,
    minHeight: 44,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
  },
  dropdown: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    minWidth: 200,
    minHeight: 44,
    gap: tokens.spacing.absolute["2"],
  },
  dropdownText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: COLORS.neutral700,
    flex: 1,
    textAlign: getTextAlign(isRTL),
  },
  dropdownMenu: {
    position: "absolute",
    top: "100%",
    end: 0,
    start: 0,
    minWidth: 200,
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    marginTop: tokens.spacing.absolute["1"],
    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
    elevation: 50,
    zIndex: 9999,
    overflow: "hidden",
  },
  dropdownItem: {
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["3"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.gray["100"],
    minHeight: 44,
    justifyContent: "center",
  },
  dropdownItemText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: COLORS.neutral700,
    textAlign: getTextAlign(isRTL),
  },
  applyFilterButton: {
    backgroundColor: tokens.colors.raw.slate["100"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    borderRadius: tokens.radii.absolute["radius-8"],
    minHeight: 44,
    justifyContent: "center",
  },
  applyFilterText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: "#0F172A",
  },
  clearFilterButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: "#FCA5A5",
    backgroundColor: tokens.colors.raw.red["50"],
    minHeight: 44,
    gap: tokens.spacing.absolute["1_5"],
  },
  clearFilterText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.red["500"],
  },

  // Cards Grid
  cardsGrid: {
    flexDirection: "row-reverse",
    flexWrap: "wrap",
    gap: tokens.spacing.absolute["4"],
  },
  cardsGridSmall: {
    gap: tokens.spacing.absolute["3"],
  },
  card: {
    width: "48.5%",
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    padding: tokens.spacing.absolute["5"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    overflow: "visible",
  },
  cardSmall: {
    width: "100%",
    padding: tokens.spacing.absolute["4"],
  },
  cardHeader: {
    flexDirection: "row-reverse",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: tokens.spacing.absolute["4"],
  },
  cardTitleSection: {
    flex: 1,
    marginStart: tokens.spacing.absolute["4"],
    alignItems: "flex-end",
  },
  cardTitle: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.medium,
    color: COLORS.neutral900,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["2"],
  },
  badge: {
    backgroundColor: "#a6813f",
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["1"],
    borderRadius: tokens.radii.absolute["radius-16"],
    marginBottom: tokens.spacing.absolute["2"],
  },
  badgeText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.white,
  },
  pendingBadge: {
    backgroundColor: '#9467BD',
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["1"],
    borderRadius: tokens.radii.absolute["radius-16"],
    marginBottom: tokens.spacing.absolute["2"],
  },
  pendingBadgeText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.white,
  },
  cardDescription: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    fontFamily: FONTS.regular,
    color: COLORS.neutral600,
    textAlign: getTextAlign(isRTL),
    lineHeight: 20,
  },

  // Stats Row
  statsRow: {
    flexDirection: "row-reverse",
    alignItems: "stretch",
    gap: tokens.spacing.absolute["2"],
    marginBottom: tokens.spacing.absolute["4"],
  },
  statBox: {
    flex: 1,
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingVertical: tokens.spacing.absolute["3"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    alignItems: "center",
  },
  statValue: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.numbers,
    fontWeight: "500",
    color: COLORS.neutral900,
    marginBottom: tokens.spacing.absolute["1"],
  },
  statLabel: {
    fontSize: tokens.typography.paragraph["paragraph caption font size"],
    fontFamily: FONTS.regular,
    color: COLORS.neutral500,
    textAlign: "center",
  },

  // Card Footer
  cardFooter: {
    flexDirection: "row-reverse",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: tokens.spacing.absolute["3"],
    borderTopWidth: tokens.spacing.absolute["px"],
    borderTopColor: tokens.colors.raw.gray["200"],
  },
  detailsLink: {
    flexDirection: "row-reverse",
    alignItems: "center",
    gap: tokens.spacing.absolute["1"],
  },
  detailsLinkText: {
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    fontFamily: FONTS.medium,
    color: COLORS.neutral600,
  },
  menuContainer: {
    position: "relative",
    zIndex: 100,
  },
  menuDropdown: {
    position: "absolute",
    bottom: "100%",
    end: 0,
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 5,
    minWidth: 120,
    marginBottom: tokens.spacing.absolute["1"],
    zIndex: 1000,
  },
  menuItem: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    gap: tokens.spacing.absolute["2"],
    borderBottomWidth: tokens.spacing.absolute["px"],
    borderBottomColor: tokens.colors.raw.gray["100"],
  },
  menuItemDanger: {
    borderBottomWidth: 0,
  },
  menuItemText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["700"],
  },
  menuItemTextDanger: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.red["500"],
  },

  // Empty State
  emptyState: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 60,
  },
  emptyStateText: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.regular,
    color: COLORS.neutral500,
    marginBottom: tokens.spacing.absolute["4"],
  },

  // Pagination
  pagination: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    marginTop: tokens.spacing.absolute["6"],
    gap: tokens.spacing.absolute["2"],
  },
  paginationButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2"],
    gap: tokens.spacing.absolute["1"],
  },
  paginationButtonDisabled: {
    opacity: 0.5,
  },
  paginationButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.gray["700"],
  },
  paginationButtonTextDisabled: {
    color: tokens.colors.raw.gray["300"],
  },
  paginationNumbers: {
    flexDirection: "row",
    alignItems: "center",
    gap: tokens.spacing.absolute["1"],
  },
  paginationNumber: {
    width: 36,
    height: 36,
    borderRadius: tokens.radii.absolute["radius-8"],
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: tokens.colors.raw.gray["100"],
  },
  paginationNumberActive: {
    backgroundColor: COLORS.primary,
  },
  paginationNumberText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.gray["700"],
  },
  paginationNumberTextActive: {
    color: tokens.colors.raw.white,
  },

  // Modal Styles
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContent: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    padding: tokens.spacing.absolute["6"],
    width: "90%",
    maxWidth: 400,
    alignItems: "center",
  },
  modalTitle: {
    fontSize: tokens.typography.paragraph["paragraph subheading font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.gray["700"],
    marginBottom: tokens.spacing.absolute["3"],
    textAlign: "center",
  },
  modalMessage: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
    textAlign: "center",
    marginBottom: tokens.spacing.absolute["6"],
  },
  modalButtons: {
    flexDirection: "row",
    gap: tokens.spacing.absolute["3"],
  },
  modalButton: {
    paddingHorizontal: tokens.spacing.absolute["6"],
    paddingVertical: tokens.spacing.absolute["3"],
    borderRadius: tokens.radii.absolute["radius-8"],
    minWidth: 100,
    minHeight: 44,
    alignItems: "center",
    justifyContent: "center",
  },
  modalButtonCancel: {
    backgroundColor: tokens.colors.raw.gray["100"],
  },
  modalButtonDelete: {
    backgroundColor: tokens.colors.raw.red["500"],
  },
  modalButtonCancelText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.gray["700"],
  },
  modalButtonDeleteText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.white,
  },
});

const styles = getStyles(true);
